<template>
  <section class="stores-compare">
    <GridContainer padding>
      <p class="stores-compare__eyebrow">Im Vergleich</p>
      <Heading
        tag="h2"
        size="lg"
        spaces-bottom-rem
        align="center"
      >
        In-App-Käufe smart per Handyrechnung.
      </Heading>
      <Paragraph
        align="center"
        class="stores-compare__lead"
      >
        Was die Bezahlung per Handyrechnung vom Kartenkauf oder der Guthabenkarte vom Kiosk
        unterscheidet.
      </Paragraph>

      <div class="stores-compare__scroll">
        <div
          class="stores-compare__table"
          role="table"
          tabindex="0"
          aria-label="Vergleich der Zahlungsmethoden"
        >
          <div
            class="stores-compare__row stores-compare__head"
            role="row"
          >
            <div class="stores-compare__head-cell"></div>
            <div
              v-for="col in columns"
              :key="col.key"
              class="stores-compare__head-cell"
              :class="{ 'stores-compare__cell--highlight': col.highlight }"
              role="columnheader"
            >
              {{ col.label }}
            </div>
          </div>

          <div
            v-for="row in rows"
            :key="row.label"
            class="stores-compare__row"
            role="row"
          >
            <div
              class="stores-compare__cell stores-compare__cell--label"
              role="cell"
            >
              {{ row.label }}
            </div>
            <div
              v-for="col in columns"
              :key="col.key"
              class="stores-compare__cell"
              :class="{ 'stores-compare__cell--highlight': col.highlight }"
              role="cell"
            >
              {{ row[col.key] }}
            </div>
          </div>
        </div>
      </div>
    </GridContainer>
  </section>
</template>

<script setup lang="ts">
import { GridContainer, Heading, Paragraph } from '@i22team/td-longtail-vue-components';

type ColumnKey = 'dcb' | 'cc' | 'voucher';

type Row = {
  label: string;
  dcb: string;
  cc: string;
  voucher: string;
};

const columns: { key: ColumnKey; label: string; highlight?: boolean }[] = [
  { key: 'dcb', label: 'Per Handyrechnung', highlight: true },
  { key: 'cc', label: 'Kreditkarte' },
  { key: 'voucher', label: 'Guthabenkarte' },
];

const rows: Row[] = [
  {
    label: 'Zahlart aktivieren',
    dcb: 'In unter 2 Minuten',
    cc: 'Karten-Setup nötig',
    voucher: 'Karte am Kiosk kaufen',
  },
  {
    label: 'Bankdaten bei Apple/Google',
    dcb: 'Nein',
    cc: 'Ja',
    voucher: 'Nein',
  },
  {
    label: 'Auf Telekom Rechnung',
    dcb: 'Ja',
    cc: 'Separate Abrechnung',
    voucher: 'Bar bezahlt',
  },
  {
    label: 'Cashback auf 1. Kauf',
    dcb: '100 %, bis 10 €',
    cc: 'Keins',
    voucher: 'Keins',
  },
  {
    label: 'Ausgaben steuern',
    dcb: 'Drittanbietersperre',
    cc: 'Kreditrahmen',
    voucher: 'Restguthaben',
  },
  {
    label: 'Restguthaben verfällt',
    dcb: 'Nein',
    cc: '—',
    voucher: 'Oft ja',
  },
];
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.stores-compare {
  padding-block: 3rem;

  @include mq($mq-md) {
    padding-block: 4rem;
  }

  &__eyebrow {
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--color-primary);
    margin-bottom: 12px;
    text-align: center;
  }

  &__lead {
    margin-bottom: 2rem;
    color: var(--color-font-subtle);
  }

  &__scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding: 2rem 1rem;
    position: relative;
  }

  &__table {
    position: relative;
    display: table;
    min-width: 600px;
    width: 100%;
    border-radius: 1rem;
    box-shadow: 0 0 0 1px var(--color-gray-200);
  }

  &__head-cell {
    display: table-cell;
    padding: 1rem 1.25rem;
    text-transform: uppercase;
    font-weight: 600;
    color: var(--color-font-subtle);
  }

  &__row {
    display: table-row;

    &:last-child .stores-compare__cell {
      border-bottom: none;
    }

    &:last-child .stores-compare__cell:first-child {
      border-radius: 0 0 0 1rem;
    }

    &:last-child .stores-compare__cell:last-child {
      border-radius: 0 0 1rem 0;
    }
  }

  &__cell {
    padding: 1rem 1.25rem;
    font-size: 15px;
    display: table-cell;
    align-items: center;
    gap: 0.5rem;
    border-bottom: 1px solid var(--color-gray-100);

    &--label {
      font-weight: 600;
    }
  }

  &__cell--highlight {
    background: var(--color-primary);
    color: var(--color-bg);
    border-bottom-color: rgba(255, 255, 255, 0.18);
  }
}
</style>
